export interface CharacterScale {
  npc: number;
  playerFallback: number;
  playerAvatar: number;
  remoteNpc: number;
  remoteAvatar: number;
}

export type LegacyCharacterScalePresetId = 'sunny-side' | 'office';

export const CHARACTER_SCALE_KEYS = [
  'npc',
  'playerFallback',
  'playerAvatar',
  'remoteNpc',
  'remoteAvatar',
] as const;

export const DEFAULT_CHARACTER_SCALE_MULTIPLIER = 1;
export const MIN_CHARACTER_SCALE_MULTIPLIER = 0.5;
export const MAX_CHARACTER_SCALE_MULTIPLIER = 2;

export const DEFAULT_SCENE_CHARACTER_SCALE: CharacterScale = {
  npc: 2.1,
  playerFallback: 2.1,
  playerAvatar: 0.8,
  remoteNpc: 2.1,
  remoteAvatar: 0.8,
};

export const MODERN_OFFICE_CHARACTER_SCALE: CharacterScale = {
  npc: 2.8,
  playerFallback: 2.8,
  playerAvatar: 1.25,
  remoteNpc: 2.8,
  remoteAvatar: 1.25,
};

export const LEGACY_CHARACTER_SCALE_PRESETS: ReadonlyArray<{
  id: LegacyCharacterScalePresetId;
  scale: CharacterScale;
}> = [
  {
    id: 'sunny-side',
    scale: DEFAULT_SCENE_CHARACTER_SCALE,
  },
  {
    id: 'office',
    scale: MODERN_OFFICE_CHARACTER_SCALE,
  },
];

const clampCharacterScaleValue = (value: number, fallback: number): number => {
  const parsed = Number.isFinite(value) ? value : fallback;
  return Math.max(0.5, Math.min(4, parsed));
};

const compareNumbers = (left: number, right: number): number => left - right;

const median = (values: number[]): number => {
  const sorted = [...values].sort(compareNumbers);
  if (sorted.length === 0) {
    return DEFAULT_CHARACTER_SCALE_MULTIPLIER;
  }

  const midpoint = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[midpoint - 1] + sorted[midpoint]) / 2;
  }

  return sorted[midpoint] ?? DEFAULT_CHARACTER_SCALE_MULTIPLIER;
};

export const normalizeCharacterScale = (value: CharacterScale): CharacterScale => {
  const npc = clampCharacterScaleValue(value.npc, DEFAULT_SCENE_CHARACTER_SCALE.npc);
  const playerFallback = clampCharacterScaleValue(
    value.playerFallback,
    DEFAULT_SCENE_CHARACTER_SCALE.playerFallback,
  );
  const playerAvatar = clampCharacterScaleValue(
    value.playerAvatar,
    DEFAULT_SCENE_CHARACTER_SCALE.playerAvatar,
  );
  const remoteNpc = clampCharacterScaleValue(
    value.remoteNpc,
    DEFAULT_SCENE_CHARACTER_SCALE.remoteNpc,
  );
  const remoteAvatar = clampCharacterScaleValue(
    value.remoteAvatar,
    DEFAULT_SCENE_CHARACTER_SCALE.remoteAvatar,
  );

  return {
    npc,
    playerFallback: Math.max(npc, playerFallback),
    playerAvatar,
    remoteNpc,
    remoteAvatar,
  };
};

export const normalizeCharacterScaleMultiplier = (value: number): number => {
  const parsed = Number.isFinite(value) ? value : DEFAULT_CHARACTER_SCALE_MULTIPLIER;
  return Math.max(MIN_CHARACTER_SCALE_MULTIPLIER, Math.min(MAX_CHARACTER_SCALE_MULTIPLIER, parsed));
};

export const resolveCharacterScaleWithMultiplier = (
  baseScale: CharacterScale,
  multiplier: number,
): CharacterScale => {
  const normalizedBaseScale = normalizeCharacterScale(baseScale);
  const normalizedMultiplier = normalizeCharacterScaleMultiplier(multiplier);

  return normalizeCharacterScale({
    npc: normalizedBaseScale.npc * normalizedMultiplier,
    playerFallback: normalizedBaseScale.playerFallback * normalizedMultiplier,
    playerAvatar: normalizedBaseScale.playerAvatar * normalizedMultiplier,
    remoteNpc: normalizedBaseScale.remoteNpc * normalizedMultiplier,
    remoteAvatar: normalizedBaseScale.remoteAvatar * normalizedMultiplier,
  });
};

export const deriveLegacyCharacterScaleModel = (
  legacyScale: CharacterScale,
): {
  baseScale: CharacterScale;
  characterScaleMultiplier: number;
  presetId: LegacyCharacterScalePresetId;
} => {
  const normalizedLegacyScale = normalizeCharacterScale(legacyScale);
  const bestPreset =
    LEGACY_CHARACTER_SCALE_PRESETS.map(({ id, scale }) => {
      const normalizedPresetScale = normalizeCharacterScale(scale);
      const ratios = CHARACTER_SCALE_KEYS.map(
        (key) => normalizedLegacyScale[key] / normalizedPresetScale[key],
      );
      const multiplier = normalizeCharacterScaleMultiplier(median(ratios));
      const effectiveScale = resolveCharacterScaleWithMultiplier(normalizedPresetScale, multiplier);
      const totalAbsoluteError = CHARACTER_SCALE_KEYS.reduce(
        (sum, key) => sum + Math.abs(normalizedLegacyScale[key] - effectiveScale[key]),
        0,
      );

      return {
        id,
        multiplier,
        scale: normalizedPresetScale,
        totalAbsoluteError,
      };
    }).sort((left, right) =>
      compareNumbers(left.totalAbsoluteError, right.totalAbsoluteError),
    )[0] ?? null;

  if (!bestPreset) {
    return {
      baseScale: normalizeCharacterScale(DEFAULT_SCENE_CHARACTER_SCALE),
      characterScaleMultiplier: DEFAULT_CHARACTER_SCALE_MULTIPLIER,
      presetId: 'sunny-side',
    };
  }

  return {
    baseScale: bestPreset.scale,
    characterScaleMultiplier: bestPreset.multiplier,
    presetId: bestPreset.id,
  };
};
